/**
 * NMS Sock Pair Packs Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 *
 * On tab click: fetches the reference product page, extracts and displays its color swatches
 * Color swatch clicks are handled by the existing nms-color-swatches.js
 */

class NMSSockPairPacks extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];

  constructor(element) {
    super();
    this.element = element;
    this.packId = element.dataset.packId || `packs-${Date.now()}`;
    this.sectionId = element.dataset.sectionId;
    this.mainSectionId = element.dataset.mainSectionId;
    this.sectionWrapperId = element.dataset.sectionWrapperId;
    this.activePack = parseInt(element.dataset.activePack, 10) || 1;

    // Store additional sections to update (same as color swatches)
    this.additionalSections = element.dataset.additionalSections || '';

    window.neemans.sockPairPacks = window.neemans.sockPairPacks || {};
    window.neemans.sockPairPacks[this.packId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    this.#setupRefs();
    this.#initializePackState();
    this.#bindPackEvents();
    this.#isInitialized = true;
  }

  #setupRefs() {
    this.refs.tabList = this.element.querySelector('[ref="tabList"]');
    this.refs.tabs = this.element.querySelectorAll('[ref="tabs[]"]');
    this.refs.swatchPanel = this.element.querySelector('[ref="swatchPanel"]');
    this.refs.swatchesContainer = this.element.querySelector('[ref="swatchesContainer"]');
    this.refs.currentPackLabel = this.element.querySelector('[ref="currentPackLabel"]');
  }

  #initializePackState() {
    this.setGlobalData(`sockPairPacks.${this.packId}`, {
      currentPack: this.activePack,
      isLoading: false,
    });
  }

  #bindPackEvents() {
    for (const listener of this.#eventListeners.filter(({ event }) => event === 'click')) {
      listener.element.removeEventListener(listener.event, listener.handler, listener.capture);
    }
    this.#eventListeners = this.#eventListeners.filter(({ event }) => event !== 'click');

    const clickElements = this.element.querySelectorAll('[data-nms-click]');

    for (const el of clickElements) {
      const handlerPath = el.getAttribute('data-nms-click');
      const [methodName, ...args] = handlerPath.split('/').filter(Boolean);

      if (this[methodName] && typeof this[methodName] === 'function') {
        const handler = event => {
          this[methodName].call(this, event, ...args);
        };

        el.addEventListener('click', handler, { capture: true });
        this.#eventListeners.push({ element: el, event: 'click', handler, capture: true });
      }
    }
  }

  // Tab click: fetch reference product and swap entire page content
  async handlePackTabClick(event, packNumber) {
    event.preventDefault();
    event.stopPropagation();

    const tab = event.currentTarget;

    if (tab.classList.contains('nms-sock-pair-packs__tab--active')) {
      return;
    }

    const productUrl = tab.dataset.productUrl;
    const productHandle = tab.dataset.productHandle;
    const productTitle = tab.dataset.productTitle;
    const packName = tab.dataset.packName;

    if (!productUrl) return;

    // Update active tab immediately
    this.#updateActiveTab(packNumber);

    // Update pack label
    if (this.refs.currentPackLabel && packName) {
      this.refs.currentPackLabel.textContent = packName;
    }

    this.setGlobalData(`sockPairPacks.${this.packId}.currentPack`, parseInt(packNumber, 10));
    this.setGlobalData(`sockPairPacks.${this.packId}.isLoading`, true);

    try {
      await this.#switchToProduct(productUrl, productHandle, productTitle);
    } catch (error) {
      console.error('[SockPairPacks] Failed to switch product:', error);
      // Fallback: redirect to the product
      window.location.href = productUrl;
    } finally {
      this.setGlobalData(`sockPairPacks.${this.packId}.isLoading`, false);
    }

    this.dispatchNMSEvent('sock-pair-packs:tab-changed', {
      packId: this.packId,
      packNumber,
      component: this,
    });
  }

  async #switchToProduct(productUrl, _productHandle, productTitle) {
    this.#showLoading();

    try {
      // Build sections parameter - include main section and any additional sections
      const sectionsToFetch = [this.mainSectionId];

      if (this.additionalSections) {
        // Split comma-separated section types and find their IDs on the page
        const additionalTypes = this.additionalSections.split(',').map(s => s.trim());

        additionalTypes.forEach(sectionType => {
          // Find section by data-section-type attribute (same approach as nms-color-swatches.js)
          const sectionElement = document.querySelector(`[data-section-type="${sectionType}"]`);
          if (sectionElement && sectionElement.dataset.sectionId) {
            // Use the explicit section ID from the data attribute
            sectionsToFetch.push(sectionElement.dataset.sectionId);
          }
        });
      }

      // Join all section IDs with comma for the API call
      const sectionsParam = sectionsToFetch.join(',');

      // Fetch the product page using Section Rendering API
      const fetchUrl = `${productUrl}?sections=${sectionsParam}`;
      const response = await fetch(fetchUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const sectionHtml = data[this.mainSectionId];

      if (!sectionHtml) {
        throw new Error('Section not found in response');
      }

      // Replace the entire section content
      const sectionWrapper = document.querySelector(`#${this.sectionWrapperId}`);
      if (sectionWrapper) {
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = sectionHtml;

        const newContent = tempContainer.querySelector(`#${this.sectionWrapperId}`);
        if (newContent) {
          sectionWrapper.innerHTML = newContent.innerHTML;
        } else {
          sectionWrapper.innerHTML = sectionHtml;
        }

        // Update URL
        this.#updateUrl(productUrl, productTitle);

        // Reinitialize components
        this.#reinitializeComponents(sectionWrapper);
      }

      // Update additional sections if they were fetched
      sectionsToFetch.slice(1).forEach(sectionId => {
        if (data[sectionId]) {
          this.#updateAdditionalSection(sectionId, data[sectionId]);
        }
      });
    } finally {
      this.#hideLoading();
    }
  }

  #updateAdditionalSection(sectionId, newHtml) {
    // Find the section wrapper by ID
    const sectionWrapper = document.querySelector(`#shopify-section-${sectionId}`);

    if (!sectionWrapper) {
      console.warn(`[SockPairPacks] Additional section not found: shopify-section-${sectionId}`);
      return;
    }

    // Create temporary container to parse new HTML
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = newHtml;
    const newSectionWrapper = tempContainer.querySelector(`#shopify-section-${sectionId}`);

    if (!newSectionWrapper) {
      console.warn(`[SockPairPacks] New section wrapper not found in response for: ${sectionId}`);
      return;
    }

    // Replace the entire section content
    sectionWrapper.innerHTML = newSectionWrapper.innerHTML;

    // Dispatch event for the updated section
    this.dispatchNMSEvent('nms:section-updated', {
      sectionId: sectionId,
      container: sectionWrapper,
      source: 'sock-pair-packs',
    });
  }

  #updateUrl(newUrl, productTitle) {
    history.replaceState(
      { product: productTitle, url: newUrl },
      productTitle,
      newUrl
    );

    if (productTitle) {
      document.title = document.title.replace(/^[^|]*/, productTitle + ' ');
    }
  }

  #reinitializeComponents(container) {
    // Reinitialize color swatches
    if (window.initNMSColorSwatches) {
      const swatchElements = container.querySelectorAll('[data-nms-color-swatches]');
      swatchElements.forEach(el => el.removeAttribute('data-initialized'));
      window.initNMSColorSwatches();
    }

    // Reinitialize sock pair packs (this component)
    const packElements = container.querySelectorAll('[data-nms-sock-pair-packs]');
    packElements.forEach(el => el.removeAttribute('data-initialized'));
    initNMSSockPairPacks();

    // Dispatch event for other components to reinitialize
    this.dispatchNMSEvent('nms:color-content-updated', {
      container,
      source: 'sock-pair-packs',
    });
  }

  #updateActiveTab(activePackNumber) {
    const allTabs = this.element.querySelectorAll('.nms-sock-pair-packs__tab');

    for (const tab of allTabs) {
      tab.classList.remove('nms-sock-pair-packs__tab--active');
      tab.setAttribute('aria-selected', 'false');
    }

    const activeTab = this.element.querySelector(`[data-pack-number="${activePackNumber}"]`);

    if (activeTab) {
      activeTab.classList.add('nms-sock-pair-packs__tab--active');
      activeTab.setAttribute('aria-selected', 'true');
    }
  }

  #showLoading() {
    if (this.element) {
      this.element.style.opacity = '0.6';
      this.element.style.pointerEvents = 'none';
      this.element.setAttribute('aria-busy', 'true');
    }
  }

  #hideLoading() {
    if (this.element) {
      this.element.style.opacity = '';
      this.element.style.pointerEvents = '';
      this.element.setAttribute('aria-busy', 'false');
    }
  }

  getCurrentPack() {
    return this.getGlobalData(`sockPairPacks.${this.packId}.currentPack`);
  }

  isLoading() {
    return this.getGlobalData(`sockPairPacks.${this.packId}.isLoading`) || false;
  }

  destroy() {
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler, listener.capture);
    }
    this.#eventListeners = [];

    if (window.neemans.sockPairPacks?.[this.packId]) {
      delete window.neemans.sockPairPacks[this.packId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

function initNMSSockPairPacks() {
  if (!window.NMSBaseClass) {
    console.error('NMSSockPairPacks: NMSBaseClass required');
    return;
  }

  const elements = document.querySelectorAll('[data-nms-sock-pair-packs]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSSockPairPacks(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSSockPairPacks);
} else {
  initNMSSockPairPacks();
}

document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-sock-pair-packs]')) {
    initNMSSockPairPacks();
  }
});

document.addEventListener('nms:color-content-updated', () => {
  initNMSSockPairPacks();
});

window.neemans = window.neemans || {};
window.neemans.NMSSockPairPacks = NMSSockPairPacks;
window.initNMSSockPairPacks = initNMSSockPairPacks;
